import apiGlobal from 'global/api.global';
import { Generic } from '../Store/Generic/GenericSlice';
import { errorToast, successToast } from 'Components/Toasts';
import { clearLogout, getAuthData, hasRole } from 'utils/auth/authUtils';
import {
  AddInfoConstant,
  EngineerReportTabsConstant,
  EngineStatus,
  ErrorCode,
  EventPrecedence,
  FuelTypes,
  OnboardingStatus,
  OperationPrecedence,
  ReportingTypeConstant,
  Roles,
  VesselTypeConstant,
} from 'shared/constants';
import CryptoJS from 'crypto-js';
import env from 'environment_system/env_system';
import { useFormikContext } from 'formik';
import { queryClient } from 'react-query/queryClient';
import { queryKeyes } from 'shared/queryKeys';
import { FormikProps } from 'formik';

export const updateObjectById = (
  array: Generic[], // Specify the type of the array elements
  idToUpdate: number,
  updatedData: any, // You might want to specify a type for updatedData as well
) => {
  const index = array.findIndex((item) => item.id === idToUpdate);
  if (index !== -1) {
    const newArray = [...array];
    newArray[index] = { ...newArray[index], ...updatedData };
    return newArray;
  }
  return array;
};

export const getRecordFromArray = (
  array: Generic[], // Specify the type of the array elements
  idToUpdate: number,
) => {
  let index = array.findIndex((item) => item.id === idToUpdate);
  if (index !== -1) {
    return array[index];
  }
};

export const replaceObject = (
  array: Generic[],
  condition: (obj: Generic) => boolean,
  newObject: Generic,
): Generic[] => {
  return array.map((obj) => (condition(obj) ? newObject : obj));
};

/**
 * Removes extra fields of the objects from array of objects
 * @param dataArray
 * @param propertiesToRemove
 * @returns
 */
export const removeProperties = (
  dataArray: any[],
  propertiesToRemove: string[],
) => {
  return dataArray.map((obj) => {
    const newObj = { ...obj };
    propertiesToRemove.forEach((property) => delete newObj[property]);
    return newObj;
  });
};

/**
 * Function will take two args which will substract subtractArray from dataArray
 * @param dataArray
 * @param subtractArray
 * @returns
 */
export const arraySubtract = (dataArray: any[], subtractArray: any[]) => {
  const subtractedArray = dataArray.filter((totalEvent: any) => {
    return !subtractArray.some(
      (onGoingEvent: any) =>
        onGoingEvent.cii_adjustment === totalEvent.cii_adjustment,
    );
  });
  return subtractedArray;
};

export const DatetoString = (dateStr: string) => {
  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/**
 * Retruns formated string if present else sends todays date
 * @param dateTime
 * @returns
 */
export const dateTimeFormat = (
  dateTime: string | Date,
  skipTime = false,
  returnEmpty = false,
  display?: boolean,
) => {
  let position = skipTime ? 10 : 16;
  if (dateTime === null || dateTime === undefined || dateTime === '') {
    return null;
  }
  try {
    if (typeof dateTime !== 'string') {
      dateTime = DatetoString(dateTime?.toString());
    }
    if (dateTime) {
      if (display && !skipTime) {
        return `${dateTime.substring(8, 10)}-${dateTime.substring(5, 7)}-${dateTime.substring(0, 4)} ${dateTime.slice(11, 16)}`;
      } else if (display && skipTime) {
        return `${dateTime.substring(8, 10)}-${dateTime.substring(5, 7)}-${dateTime.substring(0, 4)}`;
      }
      return dateTime.slice(0, position);
    }
    if (returnEmpty) {
      return '';
    }
    return new Date().toISOString().slice(0, position);
  } catch (error) {
    console.error(error);
    return new Date().toISOString().slice(0, position);
  }
};

/**
 * function for formating the bytes
 * @param bytes
 * @param decimals
 * @returns
 */
export const formatBytes = (bytes: any, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

/**
 * Sort an object based on keys
 * @param inputObject
 * @returns
 */
export const sortArrayObject = (inputObject: any) => {
  if (inputObject) {
    const keys = Object.keys(inputObject);
    keys.sort();
    const sortedObject: any = {};
    keys.forEach((key) => {
      sortedObject[key] = inputObject[key];
    });
    return sortedObject;
  } else {
    return null;
  }
};

/**
 * Return an array with unique ids from first array
 * @param original array
 * @returns array
 */
export const filterUniqueByFields = (array: any[], fields: string[]) => {
  return array?.reduce((acc, current) => {
    if (
      !acc.some((obj: any) =>
        fields.every((field) => obj[field] === current[field]),
      )
    ) {
      acc.push(current);
    }
    return acc;
  }, []);
};

/** Return CII Category range according to vessel type & dead weight
 * @param dead weight
 * @returns array
 */
export const getCIICategoryRange = (type: string, dwt: number) => {
  if (type === 'Bulk Carrier') {
    return {
      a: 0.86,
      b: 0.94,
      c: 1.06,
      d: 1.18,
      e: 1000,
    };
  } else if (type === 'Gas Carrier') {
    return {
      a: dwt >= 65000 ? 0.81 : 0.85,
      b: dwt >= 65000 ? 0.91 : 0.95,
      c: dwt >= 65000 ? 1.12 : 1.06,
      d: dwt >= 65000 ? 1.44 : 1.25,
      e: 1000,
    };
  } else if (type === 'Tanker') {
    return {
      a: 0.82,
      b: 0.93,
      c: 1.08,
      d: 1.28,
      e: 1000,
    };
  } else if (type === 'Container') {
    return {
      a: 0.83,
      b: 0.94,
      c: 1.07,
      d: 1.19,
      e: 1000,
    };
  } else if (type === 'General Cargo Ship') {
    return {
      a: 0.83,
      b: 0.94,
      c: 1.06,
      d: 1.19,
      e: 1000,
    };
  } else if (type === 'LNG Carrier') {
    return {
      type: 'LNG Carrier',
      a: dwt >= 100000 ? 0.89 : 0.78,
      b: dwt >= 100000 ? 0.98 : 0.92,
      c: dwt >= 100000 ? 1.06 : 1.1,
      d: dwt >= 100000 ? 1.13 : 1.37,
      e: 1000,
    };
  } else if (type === 'RoRo Cargo VC') {
    return {
      a: 0.86,
      b: 0.94,
      c: 1.06,
      d: 1.16,
      e: 1000,
    };
  } else if (type === 'RoRo Cargo Ship') {
    return {
      a: 0.66,
      b: 0.9,
      c: 1.11,
      d: 1.41,
      e: 1000,
    };
  }
};

/**
 * Return CII category
 * @param vessel type & CII ratio
 * @returns string
 */
export const CIICategory = (type: any, ratio: any, dwt: number) => {
  if (type === 'Bulk Carrier') {
    if (ratio >= 0 && ratio <= 0.86) {
      return 'A';
    } else if (ratio >= 0.87 && ratio <= 0.94) {
      return 'B';
    } else if (ratio >= 0.95 && ratio <= 1.06) {
      return 'C';
    } else if (ratio >= 1.07 && ratio <= 1.18) {
      return 'D';
    } else if (ratio >= 1.19) {
      return 'E';
    }
  } else if (type === 'Gas Carrier') {
    if (dwt >= 65000) {
      if (ratio >= 0 && ratio <= 0.81) {
        return 'A';
      } else if (ratio >= 0.82 && ratio <= 0.91) {
        return 'B';
      } else if (ratio >= 0.92 && ratio <= 1.12) {
        return 'C';
      } else if (ratio >= 1.13 && ratio <= 1.44) {
        return 'D';
      } else if (ratio >= 1.45) {
        return 'E';
      }
    } else if (dwt < 65000) {
      if (ratio >= 0 && ratio <= 0.85) {
        return 'A';
      } else if (ratio >= 0.86 && ratio <= 0.95) {
        return 'B';
      } else if (ratio >= 0.96 && ratio <= 1.06) {
        return 'C';
      } else if (ratio >= 1.07 && ratio <= 1.25) {
        return 'D';
      } else if (ratio >= 1.26) {
        return 'E';
      }
    }
  } else if (type === 'Tanker') {
    if (ratio >= 0 && ratio <= 0.82) {
      return 'A';
    } else if (ratio >= 0.83 && ratio <= 0.93) {
      return 'B';
    } else if (ratio >= 0.94 && ratio <= 1.08) {
      return 'C';
    } else if (ratio >= 1.09 && ratio <= 1.28) {
      return 'D';
    } else if (ratio >= 1.29) {
      return 'E';
    }
  } else if (type === 'Container') {
    if (ratio >= 0 && ratio <= 0.83) {
      return 'A';
    } else if (ratio >= 0.84 && ratio <= 0.94) {
      return 'B';
    } else if (ratio >= 0.95 && ratio <= 1.07) {
      return 'C';
    } else if (ratio >= 1.08 && ratio <= 1.19) {
      return 'D';
    } else if (ratio >= 1.2) {
      return 'E';
    }
  } else if (type === 'General Cargo Ship') {
    if (ratio >= 0 && ratio <= 0.83) {
      return 'A';
    } else if (ratio >= 0.84 && ratio <= 0.94) {
      return 'B';
    } else if (ratio >= 0.95 && ratio <= 1.06) {
      return 'C';
    } else if (ratio >= 1.07 && ratio <= 1.19) {
      return 'D';
    } else if (ratio >= 1.2) {
      return 'E';
    }
  } else if (type === 'Refrigerated Cargo Carrier') {
    if (ratio >= 0 && ratio <= 0.78) {
      return 'A';
    } else if (ratio >= 0.79 && ratio <= 0.91) {
      return 'B';
    } else if (ratio >= 0.92 && ratio <= 1.07) {
      return 'C';
    } else if (ratio >= 1.08 && ratio <= 1.2) {
      return 'D';
    } else if (ratio >= 1.21) {
      return 'E';
    }
  } else if (type === 'Combination Carrier') {
    if (ratio >= 0 && ratio <= 0.78) {
      return 'A';
    } else if (ratio >= 0.79 && ratio <= 0.96) {
      return 'B';
    } else if (ratio >= 0.97 && ratio <= 1.06) {
      return 'C';
    } else if (ratio >= 1.07 && ratio <= 1.14) {
      return 'D';
    } else if (ratio >= 1.15) {
      return 'E';
    }
  } else if (type === 'LNG Carrier') {
    if (dwt >= 100000) {
      if (ratio >= 0 && ratio <= 0.89) {
        return 'A';
      } else if (ratio >= 0.9 && ratio <= 0.98) {
        return 'B';
      } else if (ratio >= 0.99 && ratio <= 1.06) {
        return 'C';
      } else if (ratio >= 1.07 && ratio <= 1.13) {
        return 'D';
      } else if (ratio >= 1.14) {
        return 'E';
      }
    } else if (dwt < 100000) {
      if (ratio >= 0 && ratio <= 0.78) {
        return 'A';
      } else if (ratio >= 0.79 && ratio <= 0.92) {
        return 'B';
      } else if (ratio >= 0.93 && ratio <= 1.1) {
        return 'C';
      } else if (ratio >= 1.11 && ratio <= 1.37) {
        return 'D';
      } else if (ratio >= 1.38) {
        return 'E';
      }
    }
  } else if (type === 'Ro-Ro Cargo VC') {
    if (ratio >= 0 && ratio <= 0.86) {
      return 'A';
    } else if (ratio >= 0.87 && ratio <= 0.94) {
      return 'B';
    } else if (ratio >= 0.95 && ratio <= 1.06) {
      return 'C';
    } else if (ratio >= 1.07 && ratio <= 1.16) {
      return 'D';
    } else if (ratio >= 1.17) {
      return 'E';
    }
  } else if (type === 'Ro-Ro Cargo Ship') {
    if (ratio >= 0 && ratio <= 0.66) {
      return 'A';
    } else if (ratio >= 0.67 && ratio <= 0.9) {
      return 'B';
    } else if (ratio >= 0.91 && ratio <= 1.11) {
      return 'C';
    } else if (ratio >= 1.12 && ratio <= 1.41) {
      return 'D';
    } else if (ratio >= 1.42) {
      return 'E';
    }
  } else if (type === 'Ro-Ro Passenger Ship') {
    if (ratio >= 0 && ratio <= 0.72) {
      return 'A';
    } else if (ratio >= 0.73 && ratio <= 0.9) {
      return 'B';
    } else if (ratio >= 0.91 && ratio <= 1.12) {
      return 'C';
    } else if (ratio >= 1.13 && ratio <= 1.41) {
      return 'D';
    } else if (ratio >= 1.42) {
      return 'E';
    }
  } else if (type === 'Cruise Passenger Ship') {
    if (ratio >= 0 && ratio <= 0.87) {
      return 'A';
    } else if (ratio >= 0.88 && ratio <= 0.95) {
      return 'B';
    } else if (ratio >= 0.96 && ratio <= 1.06) {
      return 'C';
    } else if (ratio >= 1.07 && ratio <= 1.16) {
      return 'D';
    } else if (ratio >= 1.17) {
      return 'E';
    }
  }
};

/** HTTP responses to generate errors */
export let errResponse: number[] = [400, 401, 403, 404, 409, 500, 502];

/** Calculate local time based on UTC & time difference */
export const calculateUTCTime = (
  local_time: any,
  time_diff: any,
  id: number | null,
) => {
  if (
    time_diff !== null &&
    time_diff !== '-0' &&
    time_diff !== '+0' &&
    time_diff !== '+undefined' &&
    time_diff !== '-undefined' &&
    local_time !== null
  ) {
    let localDate;
    if (id > 0) {
      const localDateString = local_time?.split(/[-T:Z]/);
      localDate = new Date(
        Number(localDateString[0]),
        Number(localDateString[1]) - 1,
        Number(localDateString[2]),
        Number(localDateString[3]),
        Number(localDateString[4]),
      );
    } else {
      localDate = new Date(local_time);
    }
    const hours = -1 * parseInt(time_diff?.toString().substring(0, 3));
    let minutes = parseInt(time_diff?.toString().substring(4, 6));
    localDate.setHours(localDate.getHours() + hours);
    if (time_diff.toString().substring(0, 1) === '+') {
      minutes = minutes * -1;
    }
    localDate.setMinutes(localDate.getMinutes() + minutes);
    if (isNaN(localDate.getTime())) {
      return null;
    }
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, '0');
    const day = String(localDate.getDate()).padStart(2, '0');
    const localHours = String(localDate.getHours()).padStart(2, '0');
    const localMinutes = String(localDate.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${localHours}:${localMinutes}`;
  } else {
    return null;
  }
};

export const searchPorts = async (port: any) => {
  if (port.length >= 3) {
    try {
      const response = await apiGlobal.get(
        `port_master/filter_by_port_name/?port_name=${port}`,
      );
      return response.data;
    } catch (error) {
      console.error(error);
      return [];
    }
  }
};

export const searchCountries = async (country: any) => {
  if (country.length >= 3) {
    try {
      const response = await apiGlobal.get(
        `country_master/filter_by_country_name/?country_name=${country}`,
      );
      return response.data;
    } catch (error) {
      console.error(error);
      return [];
    }
  }
};

export const calculateTimeDurationBetwnReports = (
  lastRecordDate: any,
  currRecordDate: any,
) => {
  const dt1 = new Date(lastRecordDate);
  const dt2 = new Date(currRecordDate);
  return Math.floor(
    (Date.UTC(
      dt2.getFullYear(),
      dt2.getUTCMonth(),
      dt2.getUTCDate(),
      dt2.getUTCHours(),
      dt2.getUTCMinutes(),
      dt2.getUTCSeconds(),
    ) -
      Date.UTC(
        dt1.getFullYear(),
        dt1.getUTCMonth(),
        dt1.getUTCDate(),
        dt1.getUTCHours(),
        dt1.getUTCMinutes(),
        dt1.getUTCSeconds(),
      )) /
    (1000 * 60),
  );
};

/** Enable/disable fields based on machine-fuel applicability */
export const machineryEnabled = (
  fuelSettings: any[],
  VesselID: number,
  machine: number,
  fuel: number,
) => {
  if (
    fuelSettings.filter(
      (item: any) =>
        item.vessel_machinery_fc_group === machine &&
        item.fuel_type === fuel &&
        item.vessel === VesselID,
    )[0]?.selected === true
  ) {
    return false;
  }
  return true;
};

export function truncateString(str: string, maxLength: number) {
  if (str.length > maxLength) {
    return str.slice(0, maxLength - 3) + '...';
  }
  return str;
}

export function countInboxMessages(number: number) {
  if (number > 99) {
    return '99+';
  }
  return number;
}

/** Function to handle responses from server i.e. show success/error messages */
export const handleServerResponse = async (
  res: any | any[],
  showError: boolean = true,
) => {
  let results: any;
  if (Array.isArray(res)) {
    results = await Promise.allSettled(res);
  } else {
    results = await Promise.allSettled([res]);
  }
  const successCount = results.filter(
    (result: any) => result.status === 'fulfilled',
  ).length;
  const errorCount = results.filter(
    (result: any) => result.status === 'rejected',
  ).length;
  if (errorCount && errorCount > 0) {
    if (showError) {
      errorToast(
        'Something went wrong. Please check the errors for more details',
      );
    }
    return results;
  } else if (successCount && successCount > 0) {
    successToast('Data saved successfully!');
    return true;
  }
};

/** Converts gram into metic tonne */
export const gmToMt = (value: number) => {
  return value / 1000000;
};

/** Converts metic tonne into gram */
export const MtToGm = (value: number) => {
  return value * 1000000;
};

/**
 *  Check if the primary key is valid
 * @param primaryKey
 * @returns
 */
export const checkInvalidPrimaryKey = (...primaryKeys: number[]): boolean => {
  if (primaryKeys.length === 0) {
    return false;
  }
  return primaryKeys.every((key) => key !== -1 && !!key);
};

/**
 * Disable button on following condition
 * if vessel onboarding_status is configured or onboarded
 * if user is manager or master
 * @param vessel current vessel object
 * @returns true if disable
 */
export const isConfigurationButtonDisabled = (vessel: any): boolean => {
  if (
    (hasRole(Roles.MANAGER) || hasRole(Roles.MASTER)) &&
    (vessel?.onboarding_status === OnboardingStatus.CONFIGURED ||
      vessel?.onboarding_status === OnboardingStatus.ONBOARDED)
  ) {
    return true;
  }
  return false;
};
/**
 * encrypt password
 * @param password
 * @returns encrypted string
 * Use a 32-byte key (same as your Python example)
 */
const key = CryptoJS.enc.Utf8.parse(env.encription_key);

export function encrypt(plaintext: string): string {
  try {
    if (!plaintext) {
      throw new Error('Plaintext cannot be empty.');
    }
    const iv = CryptoJS.lib.WordArray.random(16);
    const encrypted = CryptoJS.AES.encrypt(plaintext, key, {
      iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });
    const combined = iv.concat(encrypted.ciphertext);
    return CryptoJS.enc.Base64.stringify(combined);
  } catch (error) {
    console.error('Encryption error:', error);
    throw error;
  }
}

/**
 * decrypt password
 * @param encrypted
 * @returns decrypted string
 */
export function decrypt(encryptedText: string): string | null {
  try {
    const encryptedBytes = CryptoJS.enc.Base64.parse(encryptedText);
    const iv = CryptoJS.lib.WordArray.create(
      encryptedBytes.words.slice(0, 4),
      16,
    );
    const ciphertext = CryptoJS.lib.WordArray.create(
      encryptedBytes.words.slice(4),
    );
    const cipherParams = CryptoJS.lib.CipherParams.create({ ciphertext });

    const decrypted = CryptoJS.AES.decrypt(cipherParams, key, {
      iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });
    const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);
    if (!decryptedText) {
      throw new Error('Decryption failed or returned an empty string.');
    }
    return decryptedText;
  } catch (error) {
    console.error('Decryption error:', error);
    return null;
  }
}
/**
 * Clears the authorization key from local storage
 */
export const clearAuthKey = (delay: number = 3 * 60 * 1000): void => {
  setTimeout(() => {
    localStorage.removeItem('Authorization_key');
    sessionStorage.removeItem('Authorization_key');
  }, delay);
};

// Utility function to check if the value is empty string and set the field value to null
export const checkEmptyStringValue = (
  event: React.ChangeEvent<HTMLInputElement>,
  fieldName: string,
  setFieldValue: (field: string, value: any) => void,
) => {
  const value = event.target.value.trim() === '' ? null : event.target.value;
  setFieldValue(fieldName, value);
};

/**
 * Generic Function to add selected value to all object
 * @param value selected value
 * @param fieldName name of the field which you want to update
 * @param formikArray formik array name
 * @param formik formik
 * @param ArrayName name of formik array
 */
export const setSelectValueToAllFields = (
  value: any,
  fieldName: string,
  formikArray: any[],
  formik: any,
  ArrayName: string,
) => {
  formikArray?.map((obj: any, index: number) =>
    formik?.setFieldValue(`${ArrayName}.${index}.${fieldName}`, value),
  );
};

export const handleRemarksPreviousButton = (
  currentVesselReport: any,
  toggleDynamicTabs: any,
  previousTabIndex: number,
  toggleAddLfondsTab: any,
  toggleAddEconTab: any,
) => {
  if (env?.additional_info_engg === AddInfoConstant.NONE) {
    toggleDynamicTabs(previousTabIndex - 1);
  } else if (env?.additional_info_engg === AddInfoConstant.LFONDS_ENGG) {
    if (
      (currentVesselReport?.event_precedence_id === EventPrecedence.NOON ||
        currentVesselReport?.only_noon_or_combine_with_other_event ===
        'combine_with_other_report') &&
      currentVesselReport.operation_precedence_id === OperationPrecedence.SEA_PASSAGE
    ) {
      toggleDynamicTabs(previousTabIndex - 1);
      toggleAddLfondsTab(3);
    } else if (
      ((currentVesselReport?.event_precedence_id !== EventPrecedence.NOON ||
        currentVesselReport?.only_noon_or_combine_with_other_event !==
        'combine_with_other_report') &&
        currentVesselReport.operation_precedence_id === OperationPrecedence.SEA_PASSAGE) ||
      ((currentVesselReport?.event_precedence_id === EventPrecedence.NOON ||
        currentVesselReport?.only_noon_or_combine_with_other_event ===
        'combine_with_other_report') &&
        currentVesselReport.operation_precedence_id !== OperationPrecedence.SEA_PASSAGE)
    ) {
      toggleDynamicTabs(previousTabIndex - 1);
      toggleAddLfondsTab(2);
    } else {
      toggleDynamicTabs(previousTabIndex - 1);
      toggleAddLfondsTab(1);
    }
  } else if (env?.additional_info_engg === AddInfoConstant.ECON_ENGG) {
    if (
      currentVesselReport?.event_precedence_id === EventPrecedence.NOON ||
      currentVesselReport?.only_noon_or_combine_with_other_event ===
      'combine_with_other_report'
    ) {
      toggleDynamicTabs(previousTabIndex - 1);
      toggleAddEconTab(3);
    } else {
      toggleDynamicTabs(previousTabIndex - 1);
      toggleAddEconTab(1);
    }
  }
};

export const handleDeckRemarksPreviousButton = (
  record: any,
  toggleTab: any,
  activeTab: number,
  toggleAddLfondsTab: any,
) => {
  if (env?.additional_info_deck === AddInfoConstant.NONE) {
    toggleTab(activeTab - 1);
  } else if (env?.additional_info_deck === AddInfoConstant.LFONDS_DECK) {
    if (record?.reporting_type === ReportingTypeConstant.ATSEA) {
      toggleTab(activeTab - 1);
      toggleAddLfondsTab(1);
    } else if (
      record?.event_precedence_id === EventPrecedence.DROP_ANCHOR ||
      record?.event_precedence_id === EventPrecedence.HEAVE_ANCHOR
    ) {
      toggleTab(activeTab - 1);
      toggleAddLfondsTab(5);
    } else if (
      record?.event_precedence_id === EventPrecedence.DEPARTURE_FROM_BERTH ||
      record?.event_precedence_id ===
      EventPrecedence.DEPARTURE_FROM_DRIFTING_PORT
    ) {
      toggleTab(activeTab - 1);
      toggleAddLfondsTab(4);
    } else {
      toggleTab(activeTab - 1);
      toggleAddLfondsTab(3);
    }
  } else if (env?.additional_info_deck === AddInfoConstant.ECON_DECK) {
    toggleTab(activeTab - 1);
    if (record?.reporting_type === ReportingTypeConstant.ATSEA) {
      toggleAddLfondsTab(1);
    }
  }
};

/** Convert degress to radians */
export const toRadians = (degrees: number) => {
  return degrees * (Math.PI / 180);
};

/** Calculate the geometric distance between two location co-ordinates */
export const calculateGeometricDistance = (
  lastReport: any,
  currReport: any,
) => {
  let lastReportLatMins = parseInt(lastReport?.position_lat_min) / 60;
  let lastReportLatSecs = parseInt(lastReport?.position_lat_sec) / 3600;
  let lastReportLatDeg = parseFloat(
    `${lastReport?.lat_direction === 'north' ? '+' : '-'}${parseFloat(lastReport?.position_lat_deg) + lastReportLatMins + lastReportLatSecs}`,
  );
  let lastReportLongMins = parseInt(lastReport?.position_long_min) / 60;
  let lastReportLongSecs = parseInt(lastReport?.position_long_sec) / 3600;
  let lastReportLongDeg = parseFloat(
    `${lastReport?.long_direction === 'east' ? '+' : '-'}${parseFloat(lastReport?.position_long_deg) + lastReportLongMins + lastReportLongSecs}`,
  );
  let currReportLatMins = parseInt(currReport?.position_lat_min) / 60;
  let currReportLatSecs = parseInt(currReport?.position_lat_sec) / 3600;
  let currReportLatDeg = parseFloat(
    `${currReport?.lat_direction === 'north' ? '+' : '-'}${parseFloat(currReport?.position_lat_deg) + currReportLatMins + currReportLatSecs}`,
  );
  let currReportLongMins = parseInt(currReport?.position_long_min) / 60;
  let currReportLongSecs = parseInt(currReport?.position_long_sec) / 3600;
  let currReportLongDeg = parseFloat(
    `${currReport?.long_direction === 'east' ? '+' : '-'}${parseFloat(currReport?.position_long_deg) + currReportLongMins + currReportLongSecs}`,
  );
  let distance =
    Math.acos(
      Math.sin(toRadians(lastReportLatDeg)) *
      Math.sin(toRadians(currReportLatDeg)) +
      Math.cos(toRadians(lastReportLatDeg)) *
      Math.cos(toRadians(currReportLatDeg)) *
      Math.cos(toRadians(currReportLongDeg - lastReportLongDeg)),
    ) * 6371;
  return parseFloat((distance * 0.539957 * 0.95)?.toFixed(4));
};

/**
 * Assign api error to formik error to show error in tooltip
 * @param errorMessages
 * @param actions
 * @param useKey
 */
export const dynamicSetFieldErrors = (
  errorMessages: any,
  actions: any,
  useKey: boolean = true,
) => {
  errorMessages = FilterErrorMessage(errorMessages, [ErrorCode.FIELD_ERROR]);
  if (errorMessages?.error) {
    errorMessages.error.forEach((errorMessage: any, index: any) => {
      if (!Array.isArray(errorMessage)) {
        const InitialObjectKey = errorMessages.api_data[index].api_url
          .split('/api/')[1]
          ?.split('/')[0];
        Object.keys(errorMessage).forEach((error: any) => {
          actions.setFieldError(
            useKey ? `${InitialObjectKey}.${error}` : error,
            errorMessages.error[index][error].message,
          );
        });
      }
    });
  }
};

/**
 * Filter error message with specific status code
 * @param errorMessages
 * @returns
 */
export const FilterErrorMessage = (
  errorMessages: any,
  errorCodes: string[],
) => {
  const filteredResponses: { error: any[]; api_data: any[] } = {
    error: [],
    api_data: [],
  };
  checkValidArray(errorMessages) && errorMessages
    ?.filter((result: any) => result.status === 'rejected')
    ?.forEach((result: any) => {
      let responseData = result?.reason?.response?.data;
      if (responseData) {
        if (!Array.isArray(responseData)) {
          responseData = [responseData];
        }
        const FileteredArrayAndUniqueErrors = Array.isArray(
          responseData,
        )
          ? responseData
          : Object.entries(responseData)
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            .filter(([key, value]: [string, any]) =>
              errorCodes.includes(value?.code),
            )
            .reduce((acc: any, [key, value]: [string, any]) => {
              acc[key] = value; // Rebuild the object with only the filtered data
              return acc;
            }, {});
        if (
          FileteredArrayAndUniqueErrors &&
          Object.keys(FileteredArrayAndUniqueErrors).length > 0
        ) {
          filteredResponses.error.push(FileteredArrayAndUniqueErrors);
          filteredResponses.api_data.push({
            payload: result.reason.config.data,
            api_url: result.reason.config.baseURL + result.reason.config.url,
            response_code: result.reason.status,
            response_message: result.reason.response.data,
            http_method: result.reason.config.method,
            loggedin_user: getAuthData('user_id'),
          });
        }
      }
    });
  if (
    filteredResponses.error.length > 0 &&
    filteredResponses.api_data.length > 0
  ) {
    return filteredResponses;
  } else {
    return null;
  }
};

/**
 * function to check if valid array or not
 * @param arr array
 * @returns boolean
 */
export const checkValidArray = (arr: any[]) => {
  return (
    Array.isArray(arr) &&
    arr.some(item => item !== undefined && item !== null)
  );
};

/**
 * function to check if valid object or not
 * @param arr array
 * @returns boolean
 */
export const checkValidObject = (obj: any) => {
  if (obj && obj !== null && obj !== undefined && typeof obj === 'object' && Object.keys(obj).length > 0) {
    return true;
  } else {
    return false;
  }
};

/**prevent NAN */
export const safeParse = (value: any): number => {
  const num = parseFloat(value);
  return isNaN(num) ? 0 : num;
};

/** Compare two objects while ignoring data types of values
 * @param a: Object
 * @param b: Object
 * @returns boolean
 */
export const compareObjectIgnoreType = (a: any, b: any): boolean => {
  if (typeof a !== typeof b) {
    // Treat numeric strings and numbers as equal
    if (!isNaN(a) && !isNaN(b)) {
      return Number(a) === Number(b);
    }
    return false;
  }
  if (typeof a !== 'object' || a === null || b === null) {
    return a === b;
  }
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (let key of aKeys) {
    if (!b.hasOwnProperty(key)) return false;
    if (!compareObjectIgnoreType(a[key], b[key])) return false;
  }
  return true;
};

/**
 * Converts time string to float
 * @param timeStr
 * @returns float
 */
export const timeToDecimal = (timeStr: string) => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return parseFloat((hours + minutes / 60).toFixed(2));
};
/**safe Edit Context import */
export function useSafeFormikContext<T>() {
  try {
    return useFormikContext<T>() ?? null;
  } catch {
    return null;
  }
}
/** is temp disabled  */
/** Function to disable fields for edit */
export const disableEditFields = (vesselState: string) => {
  if (vesselState === 'EDIT_REPORTING') {
    return {
      permanentDisable: true,
      temporaryDisable: true,
    };
  } else {
    return {
      permanentDisable: false,
      temporaryDisable: false,
    };
  }
};

/**
 * Filters array to return a sorted array with only unique numbers
 * @param array<number>
 * @returns array<number>
 */
export const sortUniqueNumbers = (arr: number[]) => {
  const set = new Set<number>(arr);
  const uniqueArr: number[] = [];
  set.forEach((value) => {
    uniqueArr.push(value);
  });
  return uniqueArr.sort((a, b) => a - b);
};

/**
 * to generate report name on specific condition
 * @param report report object
 * @returns string of generated report name
 */
export const generateReportName = (report: any) => {
  if (
    report?.cii_noon_combine_with_other_event === false &&
    report?.is_cii_adjustment_report === true &&
    report?.only_noon_or_combine_with_other_event ===
    'combine_with_other_report'
  ) {
    return 'Noon & CII Adjustment';
  } else if (
    report?.cii_noon_combine_with_other_event === true &&
    report?.only_noon_or_combine_with_other_event ===
    'combine_with_other_report'
  ) {
    return `Noon , CII Adjustment & ${report?.reporting_event_name}`;
  } else if (
    report?.only_noon_or_combine_with_other_event ===
    'combine_with_other_report' &&
    report?.cii_noon_combine_with_other_event === false
  ) {
    return `Noon & ${report?.reporting_event_name}`;
  } else if (
    report?.cii_combine_with_other_event === true &&
    report?.only_noon_or_combine_with_other_event === 'none'
  ) {
    return `CII Adjustment & ${report?.reporting_event_name}`;
  } else if (
    report?.only_noon_or_combine_with_other_event === 'only_noon_report' ||
    report?.only_noon_or_combine_with_other_event === 'none'
  ) {
    return `${report?.reporting_event_name}`;
  } else {
    return `${report?.reporting_event_name}`;
  }
};

export const getTabStatus = (tabName: string, subTabsStatus: any) => {
  return (
    subTabsStatus?.find(
      (item: any) => item.form_name.toLowerCase() === tabName.toLowerCase(),
    )?.status || ''
  );
};

/**
 * Sends a structured alert notification to selected users based on vessel reporting data.
 *
 * This function generates a formatted notification message containing vessel name, report name,
 * reporting date, alert section, field value, alert message, and any user-provided response.
 * It fetches relevant details (like vessel name, user name, and report info), builds a rich
 * HTML message, and sends it via POST to the notification endpoint.
 * @param {any} message - The alert metadata containing details such as the user, vessel reporting ID,
 *                        alert message, field value, and optional response text.
 * @param {any[]} userId - An array of user IDs to whom the alert notification should be sent.
 * @param {number} categoryId - The ID representing the notification category (e.g., alert type).
 * @param {number} VesselID - The ID of the vessel associated with the alert.
 * @returns {Promise<void>} A promise that resolves after the notification is sent or logs an error if failed.
 */
export const alertNotification = async (
  message: any,
  userId: any[],
  categoryId: number,
  VesselID: number,
  fieldLabel: string,
  fieldValueForm: string,
  reportId?: number | null,
  priority?: number,
) => {
  const vesselName = await getVesselNameById(VesselID);
  const userName = await getUserNameById(message.user);
  interface ReportInfo {
    reportName?: string;
    reportDateTime?: string;
  }
  const reportInfo: ReportInfo =
    (await getReportInfoById(message?.vessel_reporting_information)) || {};
  const reportName = reportInfo.reportName || 'N/A';
  const reportDateTime = reportInfo.reportDateTime || null;
  const userResponse =
    message.response_text?.trim() || 'No reason was provided.';
  const fieldValue = message.response_value || 'N/A';
  const alertPage = message.form_title || 'N/A';
  const alertMessage = message.alert_message || 'No alert message provided.';
  const finalMessage = `
Subject: <strong>Vessel Reporting Alert Notification</strong><br/><br/>

From: Monitoring System<br/>
To: <strong>${userName}</strong><br/>
Date: ${new Date().toLocaleString()}<br/><br/>

Dear <strong>${userName}</strong>,<br/><br/>

An alert has been generated for the vessel <strong>${vesselName}</strong> in connection with the reporting event titled <strong>${reportName ? reportName : 'No report name'}</strong>, submitted on <strong>${reportDateTime ? dateTimeFormat(reportDateTime, false, false, true) : 'No report date'}</strong>.<br/><br/>

The alert originated from the "<strong>${alertPage}</strong>" section, where the recorded value "<strong>${fieldValue}</strong>" breached the expected parameters or triggered predefined conditions.<br/><br/>

<strong>Alert Message:</strong><br/>
${alertMessage}<br/><br/>

<strong>User Reason:</strong><br/>
${userResponse}<br/><br/>

Kindly review the reported data and take the necessary corrective actions as applicable.<br/><br/>

Regards,<br/>
Monitoring System
`.trim();
  const values = {
    category: categoryId,
    current_state: 1,
    date: new Date(),
    message: finalMessage,
    priority: priority,
    title: `Alert`,
    vessel: VesselID || null,
    users: userId?.map((item: any) => ({ userId: item })),
    field_label: fieldLabel,
    field_value: fieldValueForm,
    vessel_reporting_information: reportId,
  };
  try {
    await apiGlobal.post('/notification/', values);
    successToast('Data saved successfully!');
  } catch (err) {
    console.error('Error:', err);
  }
};

/**
 * Truncate text to a maximum length
 * @param text
 * @param maxLength
 * @returns truncated text or original text if it is shorter than maxLength
 */
export const truncateText = (text: string, maxLength: number) => {
  if (!text) return '';
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
};

// Fetches the vessel name from the API.
const getVesselNameById = async (vesselId: number) => {
  const res = await apiGlobal.get(`/vessel_master/${vesselId}/`);
  return res.data?.vessel_name;
};

// Fetches the user's first and last name from the API.
const getUserNameById = async (userId: number) => {
  const res = await apiGlobal.get(`/profile/${userId}/`);
  return `${res.data?.first_name} ${res.data?.last_name}`;
};

// Fetches the report name and reporting date/time from the API.
const getReportInfoById = async (reportId: number) => {
  if (!reportId) return null;
  const res = await apiGlobal.get(`/vessel_reporting_information/${reportId}/`);
  return {
    reportName: res.data?.reporting_event_name,
    reportDateTime: res.data?.reporting_time_utc, // You might want to format this with moment or date-fns
  };
};

/**
 * Function to call edit formik's submit function
 * @param formik.submitForm
 * @param toggleTab
 * @param activeTab
 * @returns void
 */
export const saveEditForm = async (
  saveEdit: any,
  toggleTabDynamic?: any,
  previousTabIndex?: any,
  setPreviousTabIndex?: any
) => {

  await saveEdit()
    ?.then(async () => {
      await queryClient.invalidateQueries(queryKeyes.edit.getDraft.key);
      if (setPreviousTabIndex) {
        setPreviousTabIndex(previousTabIndex >= 0 ? previousTabIndex + 1 : 0);
      }
      await toggleTabDynamic(previousTabIndex >= 0 ? previousTabIndex + 1 : 0);
    })
    ?.catch((err: any) => {
      console.error(err);
    });
};

/**
 * Removes all spaces from input and updates the field value.
 */
export const handleNoSpaceInputChange = (
  e: React.ChangeEvent<HTMLInputElement>,
  form: FormikProps<any>,
  fieldName: string,
) => {
  const cleaned = e.target.value.replace(/\s/g, '');
  form.setFieldValue(fieldName, cleaned);
};

/** Check if the given string/ number is NaN or not & return value accordingly
 * @param1 value: string | number
 * @param2 decimals: number
 * @returns Number
 */
export const checkForNaN = (value: string | number, decimals: number = 2, noDecimals: boolean = false) => {
  if (typeof value === 'string') {
    if (Number.isNaN(Number(value)) || value === '') {
      return Number((0)?.toFixed(decimals));
    } else {
      return Number(noDecimals ? value : Number(value)?.toFixed(decimals));
    }
  } else {
    if (Number.isNaN(value) || value === null || value === undefined ||
      value === Number.POSITIVE_INFINITY || value === Number.NEGATIVE_INFINITY) {
      return Number(0?.toFixed(decimals));
    } else {
      return Number(noDecimals ? value : Number(value)?.toFixed(decimals));
    }
  }
};

/** Check if the given string is valid or not */
export const checkValidString = (value: any) => {
  if (value !== null && value !== undefined && value.length > 0 && value !== "undefined" && value !== "null") {
    return true;
  }
  return false;
};

/**Format  tooltip text*/
export const formattooltip = (id: string) => {
  if (!id) return '';
  return id
    .replace(/_\d+$/, '') // Remove trailing underscores with numbers
    .replace(/[0-9]/g, '') // Remove all numbers
    .replace(/_/g, ' '); // Replace underscores with spaces
};

/**
 * Calculate speed based on distance travelled and time taken
 */
export const calculateSpeed = (distance_travelled: number, time_taken: number) => {
  if (time_taken === 0) return 0; // or return null, depending on your requirements
  const taken_time_in_hours = time_taken / 60;
  return distance_travelled / taken_time_in_hours;
}

/** Calculate the sum of a specific field from given array of objects 
 * @param array - array of objects
 * @param precedence_id - field to group by
 * @param addField - field to sum
 * @returns total - sum of field in all objects
*/
export const loadArraySum = (array: any[], precedence_id: string, addField: string) => {
  if (checkValidArray(array)) {
    const total = array?.reduce((acc: any, curr: any) => {
      const precedenceField = curr?.[precedence_id];
      const sumField = curr?.[addField];
      let sumFieldFloat = checkForNaN(sumField, 4);
      acc[precedenceField] = (acc[precedenceField] ?? 0) + sumFieldFloat;
      return acc;
    }, {});
    return total;
  } else {
    return {};
  }
};

/** Function to check which Enineer Reporting tab is first for edit reporting 
 * @param record - current report object
 * @returns tabId - tab id of first tab
*/
export const enggFirstTabId = (record: any) => {
  if (record && record?.operation_precedence_id === OperationPrecedence.SEA_PASSAGE) {
    return EngineerReportTabsConstant.VoyageParameterId;
  } else if (record && (record?.is_noon_report || record?.only_noon_or_combine_with_other_event === "combine_with_other_report")) {
    return EngineerReportTabsConstant.MachineryRunningHrsId;
  } else {
    return EngineerReportTabsConstant.FuelConsumptionId;
  }
}

/** Function to check if Voyage Parameters component is applicable for current reporting */
export const isVoyageParametersApplicable = (currentVesselReport: any) => {
  if (checkValidObject(currentVesselReport) && currentVesselReport?.operation_precedence_id === OperationPrecedence.SEA_PASSAGE) {
    return true;
  }
  return false;
}

/** Function to check if Machinery Running Hours component is applicable for current reporting */
export const isRunningHoursApplicable = (currentVesselReport: any, runningHrsMachineries: any[]) => {
  if (checkValidObject(currentVesselReport) && checkValidArray(runningHrsMachineries) &&
    (currentVesselReport?.is_noon_report ||
      currentVesselReport?.only_noon_or_combine_with_other_event === "combine_with_other_report")) {
    return true;
  }
  return false;
}

/** Delete vessel's last report */
export const handleDeleteReport = async (
  reportObj: any,
  setRefreshKey: any,
  setDeletePopOverState: any,
  deletevoyage: boolean
): Promise<boolean> => {
  try {
    const res = await apiGlobal.delete(
      `/vessel_reporting_information/${reportObj.id}/?delete_voyage=${deletevoyage}`
    );
    if (res.status === 200 || res.status === 204) {
      await queryClient.invalidateQueries(queryKeyes.vessel.vesselVoyageReporting.key);
      await queryClient.invalidateQueries(queryKeyes.vessel.LastVesselReport.key);
      queryClient.invalidateQueries(queryKeyes.vessel.VesselReportingCIIAdjusment.key);
      successToast("Report deleted successfully!");
      setRefreshKey((prev: number) => prev + 1);
      setDeletePopOverState(false);
      return true; // ✅ success
    }

    return false; // fallback in case response isn’t 200/204
  } catch (err: any) {
    if (errResponse.includes(err?.response?.status)) {
      errorToast("Error deleting report!");
    } else {
      console.error("Unexpected error:", err);
    }
    return false;
  }
};

/** Function to check if STS component is applicable for current reporting */
export const isSTSApplicable = (editReportBool: boolean, CurrentVessel: any, currentVesselReport: any, STSOngoingReportingObject: any) => {
  if (!editReportBool && checkValidObject(CurrentVessel) && checkValidObject(currentVesselReport) &&
    (CurrentVessel?.vessel_type_precedence_id === VesselTypeConstant.TANKER ||
      CurrentVessel?.vessel_type_precedence_id === VesselTypeConstant.LNG_CARRIER)
    && (currentVesselReport?.is_sts_considered || checkValidArray(STSOngoingReportingObject))) {
    return true;
  } else {
    return false;
  }
}

/** Function to check if CP Warranties component is applicable for current reporting */
export const isCPWarrantiesApplicable = (CurrentVessel: any, currentVesselReport: any) => {
  if (checkValidObject(CurrentVessel) && checkValidObject(currentVesselReport) &&
    currentVesselReport.status_name === EngineStatus.ringFullAway) {
    return true;
  } else {
    return false;
  }
}

/** Function to check if CII Adjustment component is applicable for current reporting */
export const isCIIAdjustmentApplicable = (editReportBool: boolean, currentVesselReport: any, filteredCIIEventsList: any) => {
  if (!editReportBool && checkValidObject(currentVesselReport) &&
    (currentVesselReport?.is_cii_adjustment_report || checkValidArray(filteredCIIEventsList))) {
    return true;
  } else {
    return false;
  }
}

/**
 * Compare two objects deeply while ignoring backend-only fields
 * @param initialValues - Formik initialValues
 * @param currentValues - Current Formik values
 * @param ignoreKeys - Optional array of keys to ignore (default common backend fields)
 * @returns boolean - true if unchanged
 */
export const isFormUnchanged = (
  initialValues: Record<string, any>,
  currentValues: Record<string, any>,
  ignoreKeys: string[] = [
    "created_on",
    "modified_on",
    "created_by",
    "modified_by",
    "display",
  ]
): boolean => {
  const omitKeys = (obj: Record<string, any>): Record<string, any> => {
    if (!obj) return obj;
    return Object.fromEntries(
      Object.entries(obj)
        .filter(([key]) => !ignoreKeys.includes(key))
        .map(([key, value]) => [
          key,
          value && typeof value === "object" && !Array.isArray(value)
            ? omitKeys(value)
            : value,
        ])
    );
  };

  const deepEqual = (a: any, b: any): boolean => {
    // Treat numbers and strings with same numeric value as equal
    if (a != null && b != null && !Array.isArray(a) && !Array.isArray(b)) {
      if (!isNaN(Number(a)) && !isNaN(Number(b))) {
        return Number(a) === Number(b);
      }
    }

    if (a === b) return true;
    if (typeof a !== typeof b) return false;
    if (a === null || b === null) return a === b;
    if (typeof a !== "object") return a === b;
    if (Array.isArray(a) !== Array.isArray(b)) return false;

    if (Array.isArray(a)) {
      if (a.length !== b.length) return false;
      return a.every((item, index) => deepEqual(item, b[index]));
    }

    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    return aKeys.every((key) => b.hasOwnProperty(key) && deepEqual(a[key], b[key]));
  };

  return deepEqual(omitKeys(initialValues), omitKeys(currentValues));
};

/** Function to check if id is present in the data 
 * @param data - array or object to check
 * @returns boolean - true if id is present in the data
*/
export const checkIfIdIsPresent = (data: any | any[]) => {
  if (Array.isArray(data)) {
    return data?.filter((item: any) => item.id > 0)?.length > 0;
  } else {
    return !!data?.id;
  }
}
/**
 * Calculates the total of a numeric field inside a nested object
 * across an array of records.
 *
 * @param records - The array of data objects
 * @param objectKey - The nested object key (e.g., "fuel_eu_record")
 * @param fieldKey - The field inside that object (e.g., "Compliance_Balance")
 * @returns The total sum of that field across all records
 */
export function calculateNestedFieldTotal<T extends Record<string, any>>(
  records: T[],
  objectKey: string,
  fieldKey: string
): number {
  if (!Array.isArray(records)) return 0;

  return records.reduce((sum, record) => {
    const value = record?.[objectKey]?.[fieldKey];
    return sum + (typeof value === 'number' ? value : 0);
  }, 0);
}
// Helper function to calculate duration between two date-times
export const calculateDuration = (start: string, end: string) => {
  const startDate = new Date(start)
  const endDate = new Date(end)
  const diffMs = endDate.getTime() - startDate.getTime()
  const diffHrs = diffMs / (1000 * 60 * 60)
  const days = Math.floor(diffHrs / 24)
  const hours = Math.floor(diffHrs % 24)
  return `${days}d ${hours}h`
}
export const handleDeleteoffHire = async (id: number) => {
  const response = await apiGlobal.delete(queryKeyes.offHire.OffHirePeriodput.url(id));
  if (response.status === 200 || response.status === 204) {
    successToast("Offhire period deleted successfully");
    queryClient.invalidateQueries(queryKeyes.offHire.OffHirePeriodByVoyage.key);
    queryClient.invalidateQueries(queryKeyes.offHire.OffHirePeriodput.key);
    await queryClient.invalidateQueries(queryKeyes.offHire.OffHirePeriodByVessel.key);
  } else {
    errorToast("An error occurred");
  }
}

/** Function to compare two numbers in ascending order */
export const compareNumbers = (a: number, b: number) => {
  return a - b;
}

/** Function to check if Special Operation - Reefer Container is applicable for current reporting */
export const isReeferContainerApplicable = (
  CurrentVessel: any,
  currentVesselReport: any,
  reeferContainerDetails: any,
  specialOpSettingObject: any,
  reefer_container_available: boolean,
  editReportBool: boolean,
  tabsIdList: any,
) => {
  if (CurrentVessel && (CurrentVessel?.vessel_type_precedence_id === VesselTypeConstant.CONTAINER ||
    CurrentVessel?.vessel_type_precedence_id === VesselTypeConstant.GENERAL_CARGO_SHIP) && !editReportBool) {
    if (
      ((reeferContainerDetails && reeferContainerDetails?.id > 0 &&
        reeferContainerDetails?.total_onboard_container > 0) ||
        currentVesselReport?.is_this_cargo_loading_discharging === "yes") &&
      reefer_container_available === true &&
      checkValidArray(specialOpSettingObject) && specialOpSettingObject[0]?.reefer_container === true &&
      !tabsIdList.includes(EngineerReportTabsConstant?.ReeferContainerId)
    ) {
      return true;
    }
    return false;
  }
  return false;
}

export function detectMultipleFuelOilConsumption(batches: any[]): boolean {
  if (!Array.isArray(batches)) return false;

  // Filter only FUEL_OIL batches with consumption > 0
  const fuelOilBatchesWithConsumption = batches.filter(
    (batch) =>
      batch?.fuel_type === FuelTypes.FUEL_OIL &&
      Number(batch?.fuel_consumption) > 0
  );

  // Need at least 2 fuel-oil batches
  if (fuelOilBatchesWithConsumption.length < 2) return false;

  // Get unique subtypes among consumed fuel-oil batches
  const uniqueSubTypes = new Set(
    fuelOilBatchesWithConsumption.map((b) => b.fuel_sub_type)
  );

  // True only if more than one unique subtype (e.g., HFO + LFO)
  return uniqueSubTypes.size > 1;
}

/** Function to logout user from system */
export const handleLogout = () => {
  clearLogout();
  successToast('Success! Logged out successfully.');
}
/**
 * Returns default vessel value
 * @param VesselID
 * @param vessel
 * @param onboardedVessels
 * @param applicableVessels
 * @returns
 */
export const getDefaultVesselValue = (
  vesselId: string | number | undefined,
  vessel: any,
  onboardedVessels: any[],
  applicableVessels: (string | number)[] | undefined
) => {
  // Return null if vesselId doesn't exist
  if (!vesselId) {
    return null;
  }
  // Filter options the same way as in the Select component
  const filteredOptions = onboardedVessels.filter(
    (item: any) => item.display === true && applicableVessels?.includes(item?.id)
  );
  // Check if vesselId exists in the filtered options
  const isVesselInOptions = filteredOptions.some((option: any) => option.id === vesselId);
  // Return the default value only if vessel is in options, otherwise null
  if (isVesselInOptions && vessel?.vessel_name_type_name) {
    return {
      id: vesselId,
      vessel_name_type_name: vessel.vessel_name_type_name,
    };
  }

  return null;
};